import {
  Button,
  CloseButton,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  useDisclosure,
  VisuallyHidden,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import type { SnapSiteConfigEditFormDataUnvalidated } from '@snap-types/models/snap-site-config';
import type { FieldPathFn } from '@ui/components/form';
import { CircleInfoIcon } from '@ui/components/Icons';
import { ArrayActions } from '@ui/features/snap-site-config/screens/edit/components/ArrayActions';
import { FormPanel } from '@ui/features/snap-site-config/screens/edit/components/FormPanel';
import { FormPanelHeading } from '@ui/features/snap-site-config/screens/edit/components/FormPanelHeading';
import { FormRowActions } from '@ui/features/snap-site-config/screens/edit/components/FormRowActions';
import {
  type FieldArrayPath,
  get,
  useFieldArray,
  type UseFieldArrayReturn,
  useFormContext,
} from 'react-hook-form';

export function MarketIndustryForm({
  fieldPath,
}: {
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
}) {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();

  const marketIndustryAdjustmentsFieldPath = fieldPath(
    'marketIndustryAdjustments',
  );
  const defaultAdjustmentFieldPath = fieldPath('defaultAdjustment');
  const marketIndustryAdjustments = useFieldArray({
    name: marketIndustryAdjustmentsFieldPath as FieldArrayPath<SnapSiteConfigEditFormDataUnvalidated>,
    control: form.control,
  });

  return (
    <FormPanel mt={6}>
      <FormPanelHeading>Market Industry Adjustments</FormPanelHeading>

      <Flex direction="column" px={4} gap={2}>
        <FormControl
          isInvalid={!!get(form.formState.errors, defaultAdjustmentFieldPath)}
        >
          <FormLabel htmlFor={defaultAdjustmentFieldPath}>
            Default Adjustment:
          </FormLabel>

          <Input
            {...form.register(defaultAdjustmentFieldPath)}
            id={defaultAdjustmentFieldPath}
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder="Enter default adjustment"
            min={0}
          />

          <ErrorMessage
            name={defaultAdjustmentFieldPath}
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>

        {marketIndustryAdjustments.fields.length === 0 ? <EmptyState /> : null}

        {marketIndustryAdjustments.fields.map((item, index) => (
          <FormRow
            key={item.id}
            fieldPath={(fieldName) =>
              fieldPath(`marketIndustryAdjustments.${index}.${fieldName}`)
            }
            fieldActions={marketIndustryAdjustments}
            index={index}
          />
        ))}

        <FormRowActions>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              marketIndustryAdjustments.append({
                adjustment: '',
                marketCode: '',
              });
            }}
          >
            Add Adjustment
          </Button>
        </FormRowActions>
      </Flex>
    </FormPanel>
  );
}

function EmptyState() {
  const { isOpen, onClose, onToggle } = useDisclosure();

  return (
    <Flex direction="column">
      <Flex
        align="center"
        gap={2}
        p={4}
        rounded="sm"
        bg="snap.teal.50"
        flexWrap="wrap"
      >
        <CircleInfoIcon color="snap.teal.400" />
        <Text fontWeight="bold" fontSize="14px" color="snap.teal.500">
          No Market Industry Adjustments set up.
        </Text>

        <Text fontSize="14px">Add an adjustment to get started.</Text>

        <Button
          variant="outline"
          size="xs"
          colorScheme="teal"
          onClick={onToggle}
        >
          What is a Market Industry Adjustment?
        </Button>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Flex p={4} bg="mw.panelGrey" mt={4} gap={2} rounded="sm">
          <Text>
            Market Industry Adjustments allow you to apply specific price
            adjustments based on market codes. This is useful for tailoring
            prices to different market segments or industries.
          </Text>

          <CloseButton onClick={onClose} />
        </Flex>
      </Collapse>
    </Flex>
  );
}

function FormRow({
  fieldPath,
  fieldActions,
  index,
}: {
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
  fieldActions: UseFieldArrayReturn<
    SnapSiteConfigEditFormDataUnvalidated,
    `priceStrategies.${number}.priceSegmentConfigs.${number}.marketIndustryAdjustments`
  >;
  index: number;
}) {
  const form = useFormContext();

  const adjustmentFieldPath = fieldPath('adjustment');
  const marketCodeFieldPath = fieldPath('marketCode');

  return (
    <Flex gap={2} align="flex-start">
      <FormControl
        isInvalid={!!get(form.formState.errors, marketCodeFieldPath)}
      >
        <VisuallyHidden>
          <FormLabel htmlFor={marketCodeFieldPath}>Market Code:</FormLabel>
        </VisuallyHidden>

        <Input
          {...form.register(marketCodeFieldPath)}
          id={marketCodeFieldPath}
          placeholder="Enter market code"
        />

        <ErrorMessage
          errors={form.formState.errors}
          name={marketCodeFieldPath}
          render={({ message }) => (
            <FormErrorMessage>{message}</FormErrorMessage>
          )}
        />
      </FormControl>

      <FormControl
        isInvalid={!!get(form.formState.errors, adjustmentFieldPath)}
      >
        <VisuallyHidden>
          <FormLabel htmlFor={adjustmentFieldPath}>Adjustment:</FormLabel>
        </VisuallyHidden>

        <Input
          {...form.register(adjustmentFieldPath)}
          id={adjustmentFieldPath}
          type="number"
          inputMode="decimal"
          placeholder="Enter adjustment"
          step="0.01"
          min={0}
        />

        <ErrorMessage
          errors={form.formState.errors}
          name={adjustmentFieldPath}
          render={({ message }) => (
            <FormErrorMessage>{message}</FormErrorMessage>
          )}
        />
      </FormControl>

      <Flex minH="40px" alignItems="center">
        <ArrayActions
          fieldActions={fieldActions}
          index={index}
          layout="horizontal"
        />
      </Flex>
    </Flex>
  );
}
